import React, { useState, useRef } from "react";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import CustomButton from "../CustomButton/CustomButton";
import { addEducationAPI } from "../../redux/features/adminSlice";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import SelecFieldForStudyComponent from "../SelecFieldForStudyComponent/SelecFieldForStudyComponent";
import { skipClickAPI } from "../../redux/features/intructorSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { updateEducationAPI } from "../../redux/features/adminSlice";
import { useSearchParams } from "react-router-dom";

// Define libraries needed for Google Maps
const libraries = ["places"];

const PlaceAutocomplete = ({ index, setFieldValue, field }) => {
  const autocompleteRef = useRef(null);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.formatted_address) {
      setFieldValue(`education[${index}].location`, place.formatted_address);
      console.log(
        `Updated location for education[${index}]:`,
        place.formatted_address
      ); // Debug
    } else {
      console.warn("No formatted address found for selected place:", place);
    }
  };

  return (
    <div className="mb-12 mb-md-16">
      <label
        htmlFor={`education[${index}].location`}
        className="form-label black-100 fw-semibold mb-8 mb-lg-12"
      >
        Location
      </label>
      <Autocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
          console.log("Autocomplete loaded for index:", index); // Debug
        }}
        onPlaceChanged={onPlaceChanged}
        fields={["formatted_address"]}
      >
        <Field name={`education[${index}].location`}>
          {({ field: formikField }) => (
            <input
              {...formikField}
              type="text"
              id={`education[${index}].location`}
              className="form-control"
              placeholder="Enter a location"
              onChange={(e) => {
                formikField.onChange(e); // Sync manual input with Formik
                setFieldValue(`education[${index}].location`, e.target.value);
              }}
            />
          )}
        </Field>
      </Autocomplete>
      <ErrorMessage
        name={`education[${index}].location`}
        component="div"
        className="text-danger error"
      />
    </div>
  );
};

export default function EditEducationForm({
  title,
  education,
  handleClose,
  refreshDataHandler,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState(null);
  const auth = useSelector((state) => state.auth);
  const today = new Date();

  console.log("Education prop:", education);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const initialValues = {
    education: [
      {
        id: education?.id || null,
        institution_name: education?.institution_name || "",
        field_of_study: education?.field_of_study_id || "",
        location: education?.location || "",
        start_date: education?.start_date || "",
        end_date: education?.end_date || "",
      },
    ],
    about_yourself: education?.about_yourself || "",
  };

  const validationSchema = Yup.object({
    education: Yup.array()
      .of(
        Yup.object().shape({
          institution_name: Yup.string()
            .min(2, "Institution name must be at least 2 characters")
            .max(100, "Institution name must be less than 100 characters")
            .required("Institution name is required"),
          field_of_study: Yup.string().required("Field of study is required"),
          location: Yup.string()
            .min(2, "Location must be at least 2 characters")
            .max(100, "Location must be less than 100 characters")
            .required("Location is required"),
          start_date: Yup.date()
            .required("Start date is required")
            .max(Yup.ref("end_date"), "Start date must be before end date")
            .nullable(),
          end_date: Yup.date()
            .required("End date is required")
            .min(Yup.ref("start_date"), "End date must be after start date")
            .nullable(),
        })
      )
      .min(1, "At least one education entry is required"),
    about_yourself: Yup.string()
      .min(10, "About must be at least 10 characters")
      .max(500, "About must be less than 500 characters")
      .required("About yourself is required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setErrorMessage(null);
      const payload = {
        userId: auth.user_id,
        id: Number(searchParams.get("id")),
        ...values,
        education: values.education.map((edu) => ({
          ...edu,
          start_date: new Date(edu.start_date).toISOString(),
          end_date: new Date(edu.end_date).toISOString(),
          field_of_study_id: edu.field_of_study,
        })),
      };
      try {
        if (title === "Add Education") {
          const res = await dispatch(
            addEducationAPI({ payload, token: auth.token, role: auth.role })
          ).unwrap();
          if (res.success) {
            toast.success(res.message, {
              position: "top-center",
            });
            refreshDataHandler();
            handleClose();
          }
        } else if (title === "Edit Education") {
          const res = await dispatch(
            updateEducationAPI({
              payload,
              ...payload.id,
              token: auth.token,
              role: auth.role,
            })
          ).unwrap();
          if (res.success) {
            toast.success(res.message, {
              position: "top-center",
            });
            refreshDataHandler();
            handleClose();
          }
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong", {
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(
        error.message || "Failed to submit education details. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (index, education, remove, setSubmitting) => {
    try {
      setSubmitting(true);
      setErrorMessage(null);
      remove(index);
    } catch (error) {
      console.error("Error deleting education:", error);
      setErrorMessage(
        error.message || "Failed to delete education entry. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const skipNowHandler = async () => {
    try {
      const res = await dispatch(skipClickAPI({ auth })).unwrap();
      if (res.success) {
        if (location.pathname === "/add-education") {
          navigate("/add-experience");
        } else if (location.pathname === "/instructor-add-education") {
          navigate("/instructor-add-experience");
        }
      }
    } catch (error) {
      console.error("Error skipping step:", error);
      toast.error(error.message || "Failed to skip step", {
        position: "top-center",
      });
    }
  };

  if (loadError) {
    return <div>Error loading Google Maps API</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, setSubmitting, setFieldValue }) => (
        <Form className="">
          <FieldArray name="education">
            {({ push, remove }) => (
              <>
                {values.education.map((edu, index) => (
                  <div key={index} className="education-block mb-20">
                    <div className="mb-12 mb-md-16">
                      <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                        Institution name
                      </label>
                      <Field
                        className="form-control"
                        name={`education[${index}].institution_name`}
                        placeholder="Enter your institute name"
                      />
                      <ErrorMessage
                        name={`education[${index}].institution_name`}
                        component="div"
                        className="text-danger error"
                      />
                    </div>

                    <div className="mb-12 mb-md-16">
                      <SelecFieldForStudyComponent
                        name={`education[${index}].field_of_study`}
                      />
                      <ErrorMessage
                        name={`education[${index}].field_of_study`}
                        component="div"
                        className="text-danger error"
                      />
                    </div>

                    <PlaceAutocomplete
                      index={index}
                      setFieldValue={setFieldValue}
                      field={values.education[index]}
                    />

                    <div className="row">
                      <div className="col-12 col-md-6">
                        <div className="mb-12 mb-md-16">
                          <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                            Start Date
                          </label>
                          <DatePicker
                            selected={
                              values.education[index].start_date
                                ? new Date(values.education[index].start_date)
                                : null
                            }
                            onChange={(date) =>
                              setFieldValue(
                                `education[${index}].start_date`,
                                date ? format(date, "MM-dd-yyyy") : ""
                              )
                            }
                            maxDate={today}
                            dateFormat="MM-dd-yyyy"
                            className="form-control"
                            placeholderText="MM-DD-YYYY"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            onKeyDown={(e) => e.preventDefault()}
                          />
                          <ErrorMessage
                            name={`education[${index}].start_date`}
                            component="div"
                            className="text-danger error"
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="mb-12 mb-md-16">
                          <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                            End Date
                          </label>
                          <DatePicker
                            selected={
                              values.education[index].end_date
                                ? new Date(values.education[index].end_date)
                                : null
                            }
                            onChange={(date) =>
                              setFieldValue(
                                `education[${index}].end_date`,
                                date ? format(date, "MM-dd-yyyy") : ""
                              )
                            }
                            minDate={
                              values.education[index].start_date
                                ? new Date(values.education[index].start_date)
                                : null
                            }
                            maxDate={today}
                            dateFormat="MM-dd-yyyy"
                            className="form-control"
                            placeholderText="MM-DD-YYYY"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            onKeyDown={(e) => e.preventDefault()}
                          />
                          <ErrorMessage
                            name={`education[${index}].end_date`}
                            component="div"
                            className="text-danger error"
                          />
                        </div>
                      </div>
                    </div>

                    {values.education.length > 1 && (
                      <div className="d-flex justify-content-end mb-12">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm no-shadow min-w-auto border-0 py-7 px-16"
                          onClick={() =>
                            handleDelete(index, edu, remove, setSubmitting)
                          }
                          disabled={isSubmitting}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                <div className="d-flex mb-20">
                  <button
                    type="button"
                    className="add-btn p-0 bg-transparent border-0 d-flex align-items-center gap-2 text-base"
                    onClick={() =>
                      push({
                        id: null,
                        institution_name: "",
                        field_of_study: "",
                        location: "",
                        start_date: "",
                        end_date: "",
                      })
                    }
                    disabled={isSubmitting}
                  >
                    <svg
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.75"
                        y="0.957947"
                        width="22.5"
                        height="22.5"
                        rx="5.25"
                        fill="#F05203"
                        fillOpacity="0.04"
                      />
                      <rect
                        x="0.75"
                        y="0.957947"
                        width="22.5"
                        height="22.5"
                        rx="5.25"
                        stroke="#F05203"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M6 12.2079L18 12.2079"
                        stroke="#F05203"
                        strokeWidth="2.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 6.20795L12 18.2079"
                        stroke="#F05203"
                        strokeWidth="2.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Add more Education
                  </button>
                </div>
              </>
            )}
          </FieldArray>

          <div className="mb-12 mb-md-16 pt-20">
            <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
              About yourself
            </label>
            <Field
              className="form-control"
              as="textarea"
              name="about_yourself"
              placeholder="Write about yourself..."
              rows="5"
            />
            <ErrorMessage
              name="about_yourself"
              component="div"
              className="text-danger error"
            />
          </div>

          <div className="pt-12 mb-18 mb-md-24">
            <CustomButton
              type="submit"
              label={title === "Edit Education" ? "Update" : "Save"}
              classes="btn btn-primary fw-bold w-100 mb-10"
              disabled={isSubmitting}
              isSubmitting={isSubmitting}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
}
